import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Mail, Send, RotateCcw, Save, Eye, Clock, Megaphone, Check, X } from "lucide-react";

const KIND_LABEL = {
  drip: "Auto-drip",
  broadcast: "Broadcast template",
};

function VariableChips({ vars, onInsert }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="variable-chips">
      <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mr-1 self-center">Insert:</span>
      {vars.map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onInsert(v)}
          className="text-[11px] font-mono bg-[#050505] border border-[#27272A] hover:border-[#FF331F] hover:text-[#FF331F] px-2 py-0.5 rounded-sm text-zinc-300"
          data-testid={`chip-${v.replace(/[{}]/g, "")}`}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

function renderPreview(text, vars) {
  if (!text) return "";
  let out = String(text);
  for (const [k, v] of Object.entries(vars)) out = out.replaceAll(`{${k}}`, v);
  return out;
}

function PreviewDialog({ open, onClose, template, defaults }) {
  const rendered = useMemo(() => ({
    subject: renderPreview(template?.subject, defaults),
    headline: renderPreview(template?.headline, defaults),
    body: renderPreview(template?.body, defaults),
  }), [template, defaults]);
  if (!template) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-[#0F0F0F] border-[#27272A] text-white" data-testid="preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">Preview</DialogTitle>
          <DialogDescription className="text-zinc-500">Variables replaced with placeholder values.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border border-[#27272A] bg-[#050505] p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Subject</div>
            <div className="text-white">{rendered.subject}</div>
          </div>
          <div className="border border-[#27272A] bg-white text-zinc-900 p-6">
            <div className="text-xs uppercase tracking-widest font-bold text-[#FF331F] mb-2">From Coach Chris</div>
            <h2 className="font-display text-2xl uppercase tracking-tight text-zinc-900 mb-4">{rendered.headline}</h2>
            <div className="text-sm leading-relaxed whitespace-pre-wrap text-zinc-700">{rendered.body}</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TemplateCard({ tpl, supportedVars, onSaved }) {
  const { user } = useAuth();
  const [draft, setDraft] = useState({
    label: tpl.label,
    subject: tpl.subject,
    headline: tpl.headline,
    body: tpl.body,
    delay_days: tpl.delay_days,
    enabled: tpl.enabled !== false,
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState(user?.email || "");
  const bodyRef = useRef(null);

  const dirty =
    draft.subject !== tpl.subject ||
    draft.headline !== tpl.headline ||
    draft.body !== tpl.body ||
    draft.label !== tpl.label ||
    (tpl.delay_days !== undefined && draft.delay_days !== tpl.delay_days) ||
    draft.enabled !== (tpl.enabled !== false);

  const insertVar = (v) => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart ?? draft.body.length;
    const end = el.selectionEnd ?? draft.body.length;
    const next = (draft.body || "").slice(0, start) + v + (draft.body || "").slice(end);
    setDraft((d) => ({ ...d, body: next }));
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + v.length, start + v.length);
    }, 0);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        subject: draft.subject,
        headline: draft.headline,
        body: draft.body,
        label: draft.label,
        enabled: draft.enabled,
      };
      if (tpl.kind === "drip") payload.delay_days = Number(draft.delay_days);
      await api.patch(`/admin/email-templates/${tpl.key}`, payload);
      toast.success("Template saved");
      await onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) return toast.error("Enter a test email");
    setTesting(true);
    try {
      await api.post(`/admin/email-templates/${tpl.key}/test`, { to_email: testEmail.trim().toLowerCase() });
      toast.success(`Test sent to ${testEmail.trim()}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const resetTemplate = async () => {
    if (!window.confirm("Reset this template back to the original copy? Your edits will be lost.")) return;
    setResetting(true);
    try {
      await api.post(`/admin/email-templates/${tpl.key}/reset`);
      toast.success("Reset to default");
      await onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="border border-[#27272A] bg-[#0F0F0F]" data-testid={`tpl-${tpl.key}`}>
      <div className="p-5 border-b border-[#27272A] flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            {tpl.kind === "drip" ? (
              <span className="text-[10px] uppercase tracking-widest font-bold bg-[#FF331F] text-white px-2 py-0.5 inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> Day {tpl.delay_days}
              </span>
            ) : (
              <span className="text-[10px] uppercase tracking-widest font-bold bg-zinc-700 text-zinc-200 px-2 py-0.5 inline-flex items-center gap-1">
                <Megaphone className="h-3 w-3" /> Broadcast
              </span>
            )}
            <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border ${draft.enabled ? "border-emerald-700 bg-emerald-950/40 text-emerald-300" : "border-zinc-700 bg-zinc-900 text-zinc-400"}`}>
              {draft.enabled ? (<><Check className="h-3 w-3 inline" /> Enabled</>) : (<><X className="h-3 w-3 inline" /> Disabled</>)}
            </span>
          </div>
          <div className="font-display uppercase text-lg text-white tracking-tight" data-testid={`tpl-label-${tpl.key}`}>{tpl.label}</div>
          <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">{tpl.key}</div>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={draft.enabled}
            onCheckedChange={(v) => setDraft((d) => ({ ...d, enabled: v }))}
            data-testid={`enabled-${tpl.key}`}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowPreview(true)}
            className="bg-[#050505] border-[#27272A] text-zinc-300 hover:text-white rounded-sm h-9 text-xs uppercase tracking-widest font-bold"
            data-testid={`preview-${tpl.key}`}
          >
            <Eye className="h-3 w-3 mr-1.5" /> Preview
          </Button>
          <Button
            type="button"
            onClick={resetTemplate}
            disabled={resetting}
            variant="outline"
            className="bg-[#050505] border-[#27272A] text-zinc-300 hover:text-white rounded-sm h-9 text-xs uppercase tracking-widest font-bold"
            data-testid={`reset-${tpl.key}`}
          >
            <RotateCcw className="h-3 w-3 mr-1.5" /> Reset
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-xs rounded-sm"
            data-testid={`save-${tpl.key}`}
          >
            <Save className="h-3 w-3 mr-1.5" /> {saving ? "Saving…" : dirty ? "Save" : "Saved"}
          </Button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <div className="grid md:grid-cols-3 gap-3">
          <div className="md:col-span-2 space-y-1.5">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Subject line</Label>
            <Input
              value={draft.subject}
              onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
              className="bg-[#050505] border-[#27272A] rounded-sm"
              data-testid={`subject-${tpl.key}`}
            />
          </div>
          {tpl.kind === "drip" && (
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Delay (days after signup)</Label>
              <Input
                type="number"
                min="0"
                max="365"
                value={draft.delay_days ?? 0}
                onChange={(e) => setDraft((d) => ({ ...d, delay_days: Number(e.target.value) }))}
                className="bg-[#050505] border-[#27272A] rounded-sm"
                data-testid={`delay-${tpl.key}`}
              />
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Headline (big bold line at top of email)</Label>
          <Input
            value={draft.headline}
            onChange={(e) => setDraft((d) => ({ ...d, headline: e.target.value }))}
            className="bg-[#050505] border-[#27272A] rounded-sm"
            data-testid={`headline-${tpl.key}`}
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Body</Label>
          <Textarea
            ref={bodyRef}
            value={draft.body}
            onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
            rows={10}
            className="bg-[#050505] border-[#27272A] rounded-sm font-mono text-sm leading-relaxed"
            data-testid={`body-${tpl.key}`}
          />
          <VariableChips vars={supportedVars} onInsert={insertVar} />
        </div>

        {/* Send test row */}
        <div className="border-t border-[#27272A] pt-4 flex items-center gap-3 flex-wrap">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-1.5">
            <Send className="h-3 w-3" /> Send test
          </div>
          <Input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="you@example.com"
            className="bg-[#050505] border-[#27272A] rounded-sm h-9 max-w-xs"
            data-testid={`test-email-${tpl.key}`}
          />
          <Button
            type="button"
            onClick={sendTest}
            disabled={testing}
            variant="outline"
            className="bg-[#050505] border-[#27272A] text-zinc-300 hover:text-white rounded-sm h-9 text-xs uppercase tracking-widest font-bold"
            data-testid={`send-test-${tpl.key}`}
          >
            {testing ? "Sending…" : "Send test email"}
          </Button>
          {dirty && (
            <span className="text-[11px] text-amber-400">Save your changes before sending a test.</span>
          )}
        </div>
      </div>

      <PreviewDialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        template={draft}
        defaults={{ first_name: "Alex", coach_name: "Coach Chris", site_url: "https://betterthroughboxing.com" }}
      />
    </div>
  );
}

export default function EmailTemplates() {
  const [data, setData] = useState({ templates: [], supported_vars: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // "all" | "drip" | "broadcast"

  const load = async () => {
    try {
      const { data } = await api.get("/admin/email-templates");
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = data.templates.filter((t) => filter === "all" ? true : t.kind === filter);
  const dripCount = data.templates.filter((t) => t.kind === "drip").length;
  const broadcastCount = data.templates.filter((t) => t.kind === "broadcast").length;

  return (
    <div className="p-8 md:p-12" data-testid="email-templates-admin">
      <div className="mb-8 max-w-3xl">
        <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2">Bulk Email Copy</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight">Email Templates.</h1>
        <p className="text-zinc-400 text-sm mt-2">
          Edit subjects, headlines, and bodies for the auto-drip and the reusable broadcast templates. Changes go live instantly — the newsletter cron reads from here on every send.
        </p>
      </div>

      <div className="flex items-center gap-2 mb-6">
        {[
          { v: "all", label: `All (${data.templates.length})`, icon: Mail },
          { v: "drip", label: `Auto-drip (${dripCount})`, icon: Clock },
          { v: "broadcast", label: `Broadcasts (${broadcastCount})`, icon: Megaphone },
        ].map((opt) => {
          const Icon = opt.icon;
          const active = filter === opt.v;
          return (
            <button
              key={opt.v}
              type="button"
              onClick={() => setFilter(opt.v)}
              className={`text-xs uppercase tracking-widest font-bold px-3 py-2 border inline-flex items-center gap-1.5 ${
                active ? "bg-[#FF331F] border-[#FF331F] text-white" : "bg-[#050505] border-[#27272A] text-zinc-400 hover:text-white"
              }`}
              data-testid={`filter-${opt.v}`}
            >
              <Icon className="h-3 w-3" /> {opt.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : (
        <div className="space-y-4 max-w-5xl">
          {filtered.map((tpl) => (
            <TemplateCard key={tpl.key} tpl={tpl} supportedVars={data.supported_vars} onSaved={load} />
          ))}
          {filtered.length === 0 && (
            <div className="text-zinc-500 text-sm border border-dashed border-[#27272A] p-12 text-center">
              No templates in this filter.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
